import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Flag } from "./Flags";
import { MagneticButton } from "./Magnetic";
import { useElapsed } from "./Stopwatch";
import { REGIONS, WINGS, submitEnquiry } from "@/lib/site";

const RegionPicker = ({ value, onChange, label }) => (
  <div className="sm:col-span-2">
    <p className="font-mono-x mb-3 text-xs uppercase tracking-[0.2em] opacity-80">{label}</p>
    <div className="flex flex-wrap gap-2" role="radiogroup">
      {REGIONS.map((r) => (
        <button type="button" key={r.id} onClick={() => onChange(r.id)} data-testid={`region-${r.id}-btn`} aria-checked={value === r.id} role="radio"
          className={`flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm transition-colors duration-300 ${value === r.id ? "border-[#FF4D1A] bg-[#FF4D1A] text-white" : "border-current/30 hover:border-[#FF4D1A]"}`}>
          <Flag id={r.id} className="w-5" /><span>{r.label}</span>
        </button>
      ))}
    </div>
    <p className="font-mono-x mt-3 text-xs opacity-80" data-testid="routing-hint">→ lands with {WINGS[REGIONS.find((r) => r.id === value).wing].email}</p>
  </div>
);

const Field = ({ f, value, onChange }) => {
  const common = { name: f.name, value, onChange, required: f.required, "data-testid": `form-${f.name}-input`, className: "field", placeholder: f.placeholder };
  return (
    <label className={f.span === 2 ? "sm:col-span-2" : ""}>
      <span className="font-mono-x text-xs uppercase tracking-[0.2em] opacity-80">{f.label}{f.required && " *"}</span>
      {f.type === "textarea" ? <textarea rows={3} {...common} /> : f.type === "select" ? (
        <select {...common}><option value="">choose</option>{f.options.map((o) => <option key={o}>{o}</option>)}</select>
      ) : <input type={f.type || "text"} {...common} />}
    </label>
  );
};

const Done = ({ inbox, onReset }) => {
  const t = useElapsed();
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="py-10" data-testid="form-success">
      <p className="font-mono-x text-xs uppercase tracking-[0.2em] opacity-90">received · clock started</p>
      <p className="font-display mt-4 text-4xl font-bold tracking-tight sm:text-5xl">got it. <span className="font-serif-i">we're on it.</span></p>
      <p className="font-mono-x mt-6 text-3xl font-bold tabular-nums">{t}</p>
      <p className="mt-4 max-w-md opacity-85">Someone at {inbox} will reply well before that number reaches 01:00:00.</p>
      <button onClick={onReset} data-testid="form-reset-btn" className="ul-draw mt-6 text-sm font-semibold">send another</button>
    </motion.div>
  );
};

export const EnquiryForm = ({ kind, fields, regionLabel = "where's the role?", cta = "start the clock", testid }) => {
  const empty = Object.fromEntries(fields.map((f) => [f.name, ""]));
  const [data, setData] = useState(empty);
  const [region, setRegion] = useState("us");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (e) => setData((d) => ({ ...d, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = Object.fromEntries(Object.entries({ ...data, kind, region }).filter(([, v]) => v !== ""));
    try {
      const res = await submitEnquiry(payload);
      setDone(res?.data?.inbox ?? WINGS[REGIONS.find((r) => r.id === region).wing].email);
      setData(empty);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg ?? err?.response?.data?.detail ?? "That didn't go through. Email us directly and we'll still be fast.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testid}>
      <AnimatePresence mode="popLayout">
        {done ? <Done key="done" inbox={done} onReset={() => setDone(null)} /> : (
          <motion.form key="form" onSubmit={submit} exit={{ opacity: 0 }} className="grid gap-x-10 gap-y-8 sm:grid-cols-2">
            {fields.map((f) => <Field key={f.name} f={f} value={data[f.name]} onChange={set} />)}
            <RegionPicker value={region} onChange={setRegion} label={regionLabel} />
            <div className="sm:col-span-2">
              <MagneticButton type="submit" tone="signal" testid="form-submit-btn" disabled={busy}>{busy ? "sending…" : cta}</MagneticButton>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
